import { Select, Tooltip } from '@mantine/core';
import { useEditorState } from '@tiptap/react';
import type { Editor } from '@tiptap/core';
import {
	IconAlignCenter,
	IconAlignLeft,
	IconAlignRight,
	IconArrowBackUp,
	IconArrowForwardUp,
	IconBold,
	IconHighlight,
	IconIndentDecrease,
	IconIndentIncrease,
	IconItalic,
	IconList,
	IconListNumbers,
	IconUnderline,
} from '@tabler/icons-react';
import type { ReactNode } from 'react';

import { Z_INDEX } from '@dealstack/frontend/shared/mechanism/styleguide/GlobalStyling';

import {
	activeAlign,
	activeFontFamily,
	activeFontSizePt,
	FONT_FACES,
	FONT_SIZES_PT,
	INDENT_STEP_MM,
	setParagraphAlign,
	setParagraphFontFamily,
	setParagraphFontSize,
	stepParagraphIndent,
} from './editorCommands';
import type { FlowFontCategory } from './fontStack';
import type { FlowRuleToolbarActions } from './flowDocumentEditor.types';
import { selectionHasRule } from './ruleCommands';
import {
	ToolbarBar,
	ToolbarButton,
	ToolbarGroup,
	ToolbarRuleButton,
	ToolbarSeparator,
} from './flowDocumentEditor.styled';

const ICON_SIZE = 16;

/**
 * The dropdown, above the modal the editor lives in.
 *
 * The theme already lifts every `Select` dropdown to `GLOBAL_MENU` for exactly this reason —
 * v7 portals it to `<body>` at the popover layer (300) and this app's modals sit at 999 — but
 * Mantine merges `defaultProps` shallowly, so passing `comboboxProps` at all REPLACES the
 * theme's. Passing `withinPortal` alone silently dropped the z-index with it: the font and size
 * dropdowns opened behind the modal, so picking a size looked like a control that did nothing.
 */
const COMBOBOX_ABOVE_MODAL = { withinPortal: true, zIndex: Z_INDEX.GLOBAL_MENU } as const;

interface Props {
	editor: Editor | null;
	/** Shown in the dropdowns when the paragraph states no face or size of its own. */
	baseFontSizePt: number;
	baseFontFamily: FlowFontCategory;
	/** The rule buttons, at the end of the row. Absent hides them. */
	ruleActions?: FlowRuleToolbarActions;
}

/**
 * The formatting a Word document actually needs, laid out like Word's Home ribbon.
 *
 * One row, grouped: history, font, character formatting, alignment, indent, lists. Deliberately
 * short of Word — no styles gallery, no colour pickers — because everything here has to be
 * something the flow model can hold and the renderer can draw. Alignment, size, face and indent
 * are paragraph ATTRIBUTES, not editor-only state, so what the toolbar changes is what the
 * generated PDF shows.
 *
 * Every control suppresses `mousedown`. A button that took focus would collapse the selection
 * before its own handler ran, so bolding a selected phrase would bold nothing.
 */
export const EditorToolbar = ({ editor, baseFontSizePt, baseFontFamily, ruleActions }: Props) => {
	const state = useEditorState({
		editor,
		selector: ({ editor: instance }) =>
			instance === null
				? null
				: {
						bold: instance.isActive('bold'),
						italic: instance.isActive('italic'),
						underline: instance.isActive('underline'),
						highlight: instance.isActive('highlight'),
						bulletList: instance.isActive('bulletList'),
						orderedList: instance.isActive('orderedList'),
						align: activeAlign(instance),
						fontSizePt: activeFontSizePt(instance, baseFontSizePt),
						fontFamily: activeFontFamily(instance, baseFontFamily),
						canUndo: instance.can().undo(),
						canRedo: instance.can().redo(),
						hasRule: selectionHasRule(instance),
					},
	});

	if (editor === null || state === null) {
		return null;
	}

	const button = ({
		label,
		testId,
		icon,
		isActive = false,
		isDisabled = false,
		run,
	}: {
		label: string;
		testId: string;
		icon: ReactNode;
		isActive?: boolean;
		isDisabled?: boolean;
		run: () => void;
	}) => (
		<Tooltip label={label} withinPortal openDelay={400} key={testId}>
			<ToolbarButton
				type="button"
				aria-label={label}
				aria-pressed={isActive}
				data-testid={testId}
				$active={isActive}
				disabled={isDisabled}
				onMouseDown={(event) => event.preventDefault()}
				onClick={run}
			>
				{icon}
			</ToolbarButton>
		</Tooltip>
	);

	return (
		<ToolbarBar data-testid="editor-toolbar">
			<ToolbarGroup>
				{button({
					label: 'Undo',
					testId: 'toolbar-undo',
					icon: <IconArrowBackUp size={ICON_SIZE} />,
					isDisabled: !state.canUndo,
					run: () => editor.chain().focus().undo().run(),
				})}
				{button({
					label: 'Redo',
					testId: 'toolbar-redo',
					icon: <IconArrowForwardUp size={ICON_SIZE} />,
					isDisabled: !state.canRedo,
					run: () => editor.chain().focus().redo().run(),
				})}
			</ToolbarGroup>

			<ToolbarSeparator />

			<ToolbarGroup>
				<Select
					size="xs"
					w={140}
					aria-label="Font"
					data-testid="toolbar-font-family"
					comboboxProps={COMBOBOX_ABOVE_MODAL}
					data={FONT_FACES.map((face) => ({ value: face.category, label: face.label }))}
					value={state.fontFamily}
					allowDeselect={false}
					onChange={(value) => {
						const face = FONT_FACES.find((candidate) => candidate.category === value);
						if (face !== undefined) {
							setParagraphFontFamily(editor, face.category);
						}
					}}
				/>
				<Select
					size="xs"
					w={68}
					aria-label="Font size"
					data-testid="toolbar-font-size"
					comboboxProps={COMBOBOX_ABOVE_MODAL}
					data={FONT_SIZES_PT.map((size) => String(size))}
					value={String(state.fontSizePt)}
					allowDeselect={false}
					onChange={(value) => {
						const size = Number(value);
						if (Number.isFinite(size) && size > 0) {
							setParagraphFontSize(editor, size);
						}
					}}
				/>
			</ToolbarGroup>

			<ToolbarSeparator />

			<ToolbarGroup>
				{button({
					label: 'Bold',
					testId: 'toolbar-bold',
					icon: <IconBold size={ICON_SIZE} />,
					isActive: state.bold,
					run: () => editor.chain().focus().toggleBold().run(),
				})}
				{button({
					label: 'Italic',
					testId: 'toolbar-italic',
					icon: <IconItalic size={ICON_SIZE} />,
					isActive: state.italic,
					run: () => editor.chain().focus().toggleItalic().run(),
				})}
				{button({
					label: 'Underline',
					testId: 'toolbar-underline',
					icon: <IconUnderline size={ICON_SIZE} />,
					isActive: state.underline,
					run: () => editor.chain().focus().toggleUnderline().run(),
				})}
				{button({
					label: 'Highlight',
					testId: 'toolbar-highlight',
					icon: <IconHighlight size={ICON_SIZE} />,
					isActive: state.highlight,
					run: () => editor.chain().focus().toggleMark('highlight').run(),
				})}
			</ToolbarGroup>

			<ToolbarSeparator />

			<ToolbarGroup>
				{button({
					label: 'Align left',
					testId: 'toolbar-align-left',
					icon: <IconAlignLeft size={ICON_SIZE} />,
					isActive: state.align === 'left',
					run: () => setParagraphAlign(editor, 'left'),
				})}
				{button({
					label: 'Align centre',
					testId: 'toolbar-align-center',
					icon: <IconAlignCenter size={ICON_SIZE} />,
					isActive: state.align === 'center',
					run: () => setParagraphAlign(editor, 'center'),
				})}
				{button({
					label: 'Align right',
					testId: 'toolbar-align-right',
					icon: <IconAlignRight size={ICON_SIZE} />,
					isActive: state.align === 'right',
					run: () => setParagraphAlign(editor, 'right'),
				})}
			</ToolbarGroup>

			<ToolbarSeparator />

			<ToolbarGroup>
				{button({
					label: 'Decrease indent',
					testId: 'toolbar-outdent',
					icon: <IconIndentDecrease size={ICON_SIZE} />,
					run: () => stepParagraphIndent(editor, -INDENT_STEP_MM),
				})}
				{button({
					label: 'Increase indent',
					testId: 'toolbar-indent',
					icon: <IconIndentIncrease size={ICON_SIZE} />,
					run: () => stepParagraphIndent(editor, INDENT_STEP_MM),
				})}
			</ToolbarGroup>

			<ToolbarSeparator />

			<ToolbarGroup>
				{button({
					label: 'Bulleted list',
					testId: 'toolbar-bullet-list',
					icon: <IconList size={ICON_SIZE} />,
					isActive: state.bulletList,
					run: () => editor.chain().focus().toggleBulletList().run(),
				})}
				{button({
					label: 'Numbered list',
					testId: 'toolbar-ordered-list',
					icon: <IconListNumbers size={ICON_SIZE} />,
					isActive: state.orderedList,
					run: () => editor.chain().focus().toggleOrderedList().run(),
				})}
			</ToolbarGroup>

			{ruleActions !== undefined && (
				<>
					<ToolbarSeparator />
					<ToolbarGroup>
						<Tooltip
							label="Show the selected content only if…"
							withinPortal
							openDelay={400}
						>
							<ToolbarRuleButton
								type="button"
								$primary
								data-testid="toolbar-add-rule"
								onMouseDown={(event) => event.preventDefault()}
								onClick={ruleActions.onAddRule}
							>
								If…
							</ToolbarRuleButton>
						</Tooltip>
						<Tooltip
							label={
								state.hasRule
									? 'Show the selected content to everyone'
									: 'Select content that is under a rule'
							}
							withinPortal
							openDelay={400}
						>
							{/* Wrapped: a disabled button fires no mouse events, so its tooltip
							    would never open to say why it is disabled. */}
							<span>
								<ToolbarRuleButton
									type="button"
									data-testid="toolbar-clear-rule"
									disabled={!state.hasRule}
									onMouseDown={(event) => event.preventDefault()}
									onClick={ruleActions.onClearRule}
								>
									Remove rule
								</ToolbarRuleButton>
							</span>
						</Tooltip>
					</ToolbarGroup>
				</>
			)}
		</ToolbarBar>
	);
};

EditorToolbar.displayName = 'EditorToolbar';
